/* SOIL: the floor as a picture, not as dots.
 *
 * The ground tile used to carry thirteen random single pixels and two
 * pebbles, rolled per tile, and the turf a field of one-pixel streaks. At
 * any scale that reads as noise: nothing on the floor was a SHAPE, and each
 * tile rolled its own confetti. This pass paints the floor in world
 * coordinates, the way the turf is baked, in three layers:
 *
 *  1. BROAD TONE. A slow field, its coordinates pulled by a second field
 *     (domain warp, so a patch has a dragged outline and not an oval), splits
 *     the ground into four bands a few tiles across -- a touch lighter, the
 *     ground itself, a touch darker, darker still. Band edges are dithered
 *     through a blue-noise threshold: grain, not a screen door.
 *
 *  2. PLATES. Cellular noise (the distance to the nearest and second-nearest
 *     of a jittered lattice of points) draws its cell borders where the two
 *     distances are close. On dust that is cracked earth, on ash a floor of
 *     flakes, on scree cobbles with each stone its own tone. Plates appear
 *     in regions the plate field chooses, so a country is not one texture
 *     wall to wall.
 *
 *  3. MOTIFS. Small authored stamps of the country's material -- a pebble
 *     with a lit top and a shadow under it, a spill of three, a chip, a
 *     crack, a hummock -- placed on the crests of a ridged field, so they run
 *     in lines the way litter is deposited, and thicker at the foot of a
 *     cliff, where scree actually gathers. Never an isolated pixel: every
 *     stamp is at least a lit cell over a shaded one, which is what makes a
 *     pebble a pebble rather than a speck.
 *
 * Seeded by world position, so a spill is the same spill when you come back. */
import { mix } from './rng.ts';
import { T, TS } from './art.ts';
import { mixHex, field } from './grass.ts';
import { BLUE32 } from './blue.ts';

const BAND_TILES = new Set([T.GROUND]);
const STAMP_TILES = new Set([T.GROUND, T.SAND, T.SCORCH, T.DEBRIS, T.TUFT, T.BONES]);

/** Blue-noise threshold at a world pixel, 0..1, toroidal over 32 pixels. */
const blue = (x, y) => BLUE32[((y & 31) << 5) | (x & 31)] / 1024;

/** Domain-warped field: the sample point is pushed by two more fields first. */
function warped(seed, x, y, scale, amp) {
  const dx = (field(seed + 31, x, y, scale * 1.7) - 0.5) * amp;
  const dy = (field(seed + 67, x, y, scale * 1.7) - 0.5) * amp;
  return field(seed, x + dx, y + dy, scale);
}

/** Ridged: the field folded about its middle, so its crests are lines. */
const ridged = (seed, x, y, scale) => 1 - Math.abs(2 * field(seed, x, y, scale) - 1);

/** Cellular noise: distance to the nearest and second-nearest jittered
 *  lattice point, and which point, for a per-cell tone. */
function cells(seed, x, y, size) {
  const ix = Math.floor(x / size), iy = Math.floor(y / size);
  let f1 = Infinity, f2 = Infinity, id = 0;
  for (let oy = -1; oy <= 1; oy++) for (let ox = -1; ox <= 1; ox++) {
    const cx = ix + ox, cy = iy + oy;
    const h = mix(seed, cx * 3 + 11, cy * 7 + 5) >>> 0;
    const px = (cx + ((h & 255) / 255) * 0.8 + 0.1) * size;
    const py = (cy + (((h >>> 8) & 255) / 255) * 0.8 + 0.1) * size;
    const d = Math.hypot(x - px, y - py);
    if (d < f1) { f2 = f1; f1 = d; id = h; } else if (d < f2) f2 = d;
  }
  return { f1, f2, id };
}

/* '+' lit, '#' shade, 'o' deeper shade, '.' nothing. Every stamp is authored
 * so the light comes from above: lit cells sit on top of shaded ones. */
const MOTIFS: Record<string, string[]> = {
  pebble:  ['.++.',
            '#oo#'],
  pebbles: ['..++...',
            '.#oo#+.',
            '.....o#'],
  chip:    ['+o',
            'o#'],
  crack:   ['#....',
            '.#+..',
            '..##.',
            '....#'],
  hummock: ['..+++..',
            '.+###o.',
            '#ooooo#'],
  flake:   ['.+#',
            '#o.'],
};

/* What lies on each country's floor, as weights, and how thickly; and what
 * its plates are. A green country's floor is mostly under turf, so it gets
 * little, soft, and no plates. */
type Plates = { size: number; kind: 'crack' | 'flake' | 'cobble'; where: number } | null;
const LITTER: Record<string, { kinds: [string, number][]; thresh: number; plates: Plates }> = {
  dust:  { kinds: [['pebble', 3], ['pebbles', 2], ['crack', 2], ['chip', 1]], thresh: 0.72,
    plates: { size: 12, kind: 'crack', where: 0.60 } },
  scree: { kinds: [['pebbles', 3], ['chip', 3], ['pebble', 2], ['crack', 1]], thresh: 0.70,
    plates: { size: 11, kind: 'cobble', where: 0.58 } },   // eleven, not seven (LS-396, 2026-09-12): at seven the seams between cobbles lined up into a grid of dark wedges at the game's zoom
  ash:   { kinds: [['flake', 3], ['crack', 2], ['chip', 1], ['pebble', 1]], thresh: 0.72,
    plates: { size: 9, kind: 'flake', where: 0.55 } },
  heath: { kinds: [['pebble', 1], ['hummock', 2]], thresh: 0.82, plates: null },
  bloom: { kinds: [['pebble', 1], ['hummock', 1]], thresh: 0.84, plates: null },
  fen:   { kinds: [['hummock', 2], ['pebble', 1]], thresh: 0.80, plates: null },
};

function inksFor(pal) {
  const ground = pal.ground || pal.floor || '#7fae69';
  const shade = pal.shade || mixHex(ground, '#000000', 0.25);
  return {
    ground, shade,
    deep: pal.rock2 || mixHex(shade, '#000000', 0.3),
    lit: pal.grit || mixHex(ground, '#ffffff', 0.22),
    bands: [mixHex(ground, pal.grit || '#ffffff', pal.grit ? 0.22 : 0.07), ground,
      mixHex(ground, shade, 0.14), mixHex(ground, shade, 0.30)],
    // a cobble's own tone, one of three, and the seam between two
    cobble: [mixHex(ground, shade, 0.08), mixHex(ground, pal.grit || '#ffffff', pal.grit ? 0.12 : 0.05), mixHex(ground, shade, 0.2)],
    seam: mixHex(ground, shade, 0.38),   // a seam is a shade darker than the ground, not a line drawn round every stone (LS-396)
  };
}

/** Bake the floor of one screen. `palOf(tx, ty)` may return a different
 *  palette per tile (the biome blend at a border); `biomeOf(tx, ty)` likewise. */
/* `palsPx` and `palOfLabel` (LS-218): the palette field, one label per pixel,
 * and the ink palette a label stands for. The soil's inks were the tile's
 * primary biome, so a screen where the blend crossed from heath to fen had
 * its bare earth change colour on one ruled line; with the field the change
 * meanders like every other edge. */
export function bakeSoil(tiles, cols, rows, pal, biome, seedNum, ox, oy, palOf = null, biomeOf = null, kinds = null, palsPx = null, palOfLabel = null) {
  const W = cols * TS, H = rows * TS;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const g = c.getContext('2d');
  const inkCache = new Map();
  const inksAt = (tx, ty) => {
    const p = palOf ? (palOf(tx, ty) || pal) : pal;
    let k = inkCache.get(p);
    if (!k) { k = inksFor(p); inkCache.set(p, k); }
    return k;
  };
  const litterAt = (tx, ty) => LITTER[biomeOf ? (biomeOf(tx, ty) || biome) : biome] || LITTER.heath;
  const inkByLabel = new Map();
  const inksLabel = (lb) => { let k = inkByLabel.get(lb); if (!k) { k = inksFor(palOfLabel(lb) || pal); inkByLabel.set(lb, k); } return k; };
  const wx0 = ox * TS, wy0 = oy * TS;
  const at = (tx, ty) => (tx < 0 || ty < 0 || tx >= cols || ty >= rows) ? -1 : tiles[ty][tx];

  // 1. broad tone, on bare ground only; 2. plates, where the plate field says.
  // PAINTED THROUGH IMAGEDATA (LS-188): a fillRect per pixel was half of every
  // screen's bake. The slow fields are sampled once per 2x2 block, the fine
  // field and the blue-noise threshold per pixel.
  // the soak's stand-in context has no ImageData; it takes the slow road
  const img = g.createImageData ? g.createImageData(W, H) : null;
  const px = img && img.data ? img.data : null;
  const hexRGB = (c: string) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
  const rgbCache = new Map<string, number[]>();
  const rgb = (c: string) => { let v = rgbCache.get(c); if (!v) { v = hexRGB(c); rgbCache.set(c, v); } return v; };
  const set = px
    ? (sx: number, sy: number, c: string) => { const v = rgb(c), i = (sy * W + sx) * 4; px[i] = v[0]; px[i + 1] = v[1]; px[i + 2] = v[2]; px[i + 3] = 255; }
    : (sx: number, sy: number, c: string) => { g.fillStyle = c; g.fillRect(sx, sy, 1, 1); };
  for (let ty = 0; ty < rows; ty++) for (let tx = 0; tx < cols; tx++) {
    // the bands and plates are painted where the GROUND FIELD says bare ground is (LS-218), not where the cell does:
    // with per-pixel kinds a tile is walked whenever any of its pixels is a band kind
    const gated = !kinds ? BAND_TILES.has(tiles[ty][tx]) : (() => { for (let y = 0; y < TS; y++) for (let x = 0; x < TS; x++) if (BAND_TILES.has(kinds[(ty * TS + y) * W + tx * TS + x])) return true; return false; })();
    if (!gated) continue;
    const k = inksAt(tx, ty);
    const lit = litterAt(tx, ty);
    const pl = lit.plates;
    for (let by2 = 0; by2 < TS; by2 += 2) for (let bx2 = 0; bx2 < TS; bx2 += 2) {
      const kk = palsPx && palOfLabel ? inksLabel(palsPx[(ty * TS + by2) * W + tx * TS + bx2]) : k;
      const wx0b = wx0 + tx * TS + bx2, wy0b = wy0 + ty * TS + by2;
      // the slow fields, once per block
      const coarse = warped(seedNum + 911, wx0b + 1, wy0b + 1, 1 / 40, 26) * 0.8;
      const region = pl ? warped(seedNum + 421, wx0b + 1, wy0b + 1, 1 / 48, 30) : 0;
      const plated = pl && region >= pl.where;
      const fade = plated ? Math.min(1, (region - pl.where) * 8) : 0;
      for (let dy = 0; dy < 2; dy++) for (let dx = 0; dx < 2; dx++) {
        const x = bx2 + dx, y = by2 + dy;
        const wx = wx0b + dx, wy = wy0b + dy;
        const v = coarse + field(seedNum + 913, wx, wy, 1 / 9) * 0.2;
        let b = Math.max(0, Math.min(3, Math.floor(v * 4)));
        const frac = v * 4 - b;
        if (frac < 0.25 && b > 0 && blue(wx, wy) > frac * 4) b--;
        const sx = tx * TS + x, sy = ty * TS + y;
        if (kinds && !BAND_TILES.has(kinds[sy * W + sx])) continue;   // this pixel is moss or water by the field
        if (b !== 1) set(sx, sy, kk.bands[b]);
        if (!plated) continue;
        const cl = cells(seedNum + 977, wx, wy, pl.size);
        const seam = cl.f2 - cl.f1;
        if (pl.kind === 'cobble') {
          if (seam < 1.0) { if (blue(wx, wy) < fade) set(sx, sy, kk.seam); }
          else if (blue(wx + 7, wy + 3) < fade * 0.9) set(sx, sy, kk.cobble[cl.id % 3]);
        } else if (pl.kind === 'crack') {
          if (seam < 0.75 && blue(wx, wy) < fade) set(sx, sy, kk.shade);
          else if (seam < 1.6 && seam >= 0.75 && (cl.id & 1) && blue(wx, wy) < fade * 0.5) set(sx, sy, kk.lit);
        } else if (seam < 1.2 && blue(wx, wy) < fade) set(sx, sy, kk.shade);
      }
    }
  }
  if (px) g.putImageData(img, 0, 0);

  // 3. motifs, on the crests of a ridged field, thicker at a cliff's foot
  const stampFits = (px, py, w, h) => {
    for (const [dx, dy] of [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]]) {
      const tx = Math.floor((px + dx) / TS), ty = Math.floor((py + dy) / TS);
      if (!STAMP_TILES.has(at(tx, ty))) return false;
    }
    return true;
  };
  const cliffFoot = (tx, ty) => at(tx, ty - 1) === T.CLIFF || at(tx, ty - 2) === T.CLIFF;
  const CELL = 9;
  for (let cy = 0; cy < Math.ceil(H / CELL); cy++) for (let cx = 0; cx < Math.ceil(W / CELL); cx++) {
    const gx = Math.floor(wx0 / CELL) + cx, gy = Math.floor(wy0 / CELL) + cy;
    const h = mix(seedNum, gx * 7 + 3, gy * 13 + 5) >>> 0;
    const jx = h & 7, jy = (h >>> 3) & 7;
    const px = gx * CELL + jx - wx0, py = gy * CELL + jy - wy0;
    const tx = Math.floor(px / TS), ty = Math.floor(py / TS);
    if (tx < 0 || ty < 0 || tx >= cols || ty >= rows) continue;
    const lit = litterAt(tx, ty);
    const crest = ridged(seedNum + 577, px + wx0, py + wy0, 1 / 30);
    const thresh = lit.thresh - (cliffFoot(tx, ty) ? 0.16 : 0);
    if (crest < thresh) continue;
    const total = lit.kinds.reduce((s, [, w]) => s + w, 0);
    let pick = ((h >>> 6) % total), kind = lit.kinds[0][0];
    for (const [k2, w] of lit.kinds) { if (pick < w) { kind = k2; break; } pick -= w; }
    const m = MOTIFS[kind];
    const w = m[0].length, hgt = m.length;
    const flip = (h >>> 12) & 1;
    if (!stampFits(px, py, w, hgt)) continue;
    const k = inksAt(tx, ty);
    for (let y = 0; y < hgt; y++) for (let x = 0; x < w; x++) {
      const ch = m[y][flip ? w - 1 - x : x];
      if (ch === '.') continue;
      g.fillStyle = ch === '+' ? k.lit : ch === '#' ? k.shade : k.deep;
      g.fillRect(px + x, py + y, 1, 1);
    }
  }
  return c;
}
